import type { Thunk, Action } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import type {
  FetchAssetParams,
  ThirdPartyBillingAccount,
  ThirdPartyBillingAccountCreate,
  ThirdPartyBillingAccountUpdate,
} from '../types';
import parseStatusCode from '../../errors/parseStatusCode';
import type { PartnerThirdPartyBillingAccountSet } from '@shipium-corp/shipium-ts-types';

export interface ThirdPartyBillingAccountModel {
  thirdPartyBillingAccounts: Record<string, ThirdPartyBillingAccount>;
  createThirdPartyBillAccount: Thunk<
    ThirdPartyBillingAccountModel,
    ThirdPartyBillingAccountCreate,
    Injections,
    Promise<ThirdPartyBillingAccount>
  >;
  fetchThirdPartyBillingAccounts: Thunk<
    ThirdPartyBillingAccountModel,
    FetchAssetParams,
    Injections,
    Promise<ThirdPartyBillingAccount[]>
  >;
  updateThirdPartyBillingAccount: Thunk<
    ThirdPartyBillingAccountModel,
    ThirdPartyBillingAccountUpdate,
    Injections,
    Promise<ThirdPartyBillingAccount>
  >;
  thirdPartyBillingActionStart: Action<ThirdPartyBillingAccountModel, void>;
  lastAction?: number;
  addThirdPartyBillingAccount: Action<ThirdPartyBillingAccountModel, ThirdPartyBillingAccount>;
  setThirdPartyBillingAccounts: Action<ThirdPartyBillingAccountModel, ThirdPartyBillingAccount[]>;
  thirdPartyBillingAccountFailure: Action<ThirdPartyBillingAccountModel, ApplicationErrorOptions>;
  fetchThirdPartyBillingAccountSets: Thunk<
    ThirdPartyBillingAccountModel,
    FetchAssetParams,
    Injections,
    Promise<ThirdPartyBillingAccount[]>
  >;
  thirdPartyBillingAccountSets: Record<string, Required<PartnerThirdPartyBillingAccountSet>>;
  setThirdPartyBillingAccountSets: Action<
    ThirdPartyBillingAccountModel,
    Required<PartnerThirdPartyBillingAccountSet>[]
  >;
}

export const thirdPartyBillingAccountModel: ThirdPartyBillingAccountModel = {
  thirdPartyBillingAccounts: {},
  thirdPartyBillingAccountSets: {},
  thirdPartyBillingAccountFailure: action((state, payload) => {
    console.error(payload);
  }),
  // add or update ThirdPartyBillingAccount in cache
  addThirdPartyBillingAccount: action((state, payload) => {
    state.thirdPartyBillingAccounts = {
      ...state.thirdPartyBillingAccounts,
      [payload.thirdPartyBillingAccountId]: payload,
    };
  }),
  // assert a list of ThirdPartyBillingAccount represents what is in cached
  setThirdPartyBillingAccounts: action((state, payload) => {
    state.thirdPartyBillingAccounts = {};
    for (const val of payload) {
      state.thirdPartyBillingAccounts[val.thirdPartyBillingAccountId] = val;
    }
  }),
  setThirdPartyBillingAccountSets: action((state, payload) => {
    state.thirdPartyBillingAccountSets = {};
    for (const val of payload) {
      state.thirdPartyBillingAccountSets[val.thirdPartyBillingAccountSetId] = val;
    }
  }),
  thirdPartyBillingActionStart: action((state, payload) => {
    state.lastAction = Date.now();
  }),
  createThirdPartyBillAccount: thunk(async (actions, payload, { injections }) => {
    try {
      const res = await injections.assetService.createThirdPartyBillingAccount(payload);
      actions.addThirdPartyBillingAccount(res);
      return res;
    } catch (error) {
      actions.thirdPartyBillingAccountFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured creating third party billing account.',
        asNotification: true,
      });
      throw error;
    }
  }),
  fetchThirdPartyBillingAccounts: thunk(async (actions, payload, { injections }) => {
    try {
      const res = await injections.assetService.fetchThirdPartyBillingAccounts();
      actions.setThirdPartyBillingAccounts(res);
      return res;
    } catch (error) {
      actions.thirdPartyBillingAccountFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching third party billing accounts.',
        asNotification: true,
      });
    }
  }),
  fetchThirdPartyBillingAccountSets: thunk(async (actions, payload, { injections }) => {
    try {
      const res = await injections.assetService.fetchThirdPartyBillingAccountSets(payload);
      actions.setThirdPartyBillingAccountSets(res);
      return res;
    } catch (error) {
      actions.thirdPartyBillingAccountFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating third party billing account sets',
        asNotification: true,
      });
      throw error;
    }
  }),
  updateThirdPartyBillingAccount: thunk(async (actions, payload, { injections }) => {
    try {
      const res = await injections.assetService.updateThirdPartyBillingAccount(payload);
      actions.addThirdPartyBillingAccount(res);
      return res;
    } catch (error) {
      actions.thirdPartyBillingAccountFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating third party billing account',
        asNotification: true,
      });
      throw error;
    }
  }),
};
